import type { FC, ReactNode, SelectHTMLAttributes } from 'react'

import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    tone?: 'light' | 'dark'
}

/**
 * A native select rather than a custom listbox. It opens the platform picker
 * on mobile (where most of this traffic comes from), works without
 * JavaScript, and cannot desynchronise between server and client render.
 */
export const NativeSelect: FC<NativeSelectProps> = ({ className, children, tone = 'light', ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select
                className={cn(
                    'h-11 w-full appearance-none rounded-lg border pl-3 pr-9 text-sm outline-none transition-colors',
                    'focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-primary/20',
                    tone === 'dark'
                        ? 'border-ink-border bg-ink-raised text-ink-foreground'
                        : 'border-input bg-card text-foreground',
                    className
                )}
                {...props}
            >
                {children}
            </select>

            <ChevronDown
                className={cn(
                    'pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2',
                    tone === 'dark' ? 'text-ink-muted' : 'text-muted-foreground'
                )}
                aria-hidden='true'
            />
        </div>
    )
}

interface FieldProps {
    label: string
    htmlFor?: string
    hint?: string
    error?: string
    required?: boolean
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, htmlFor, hint, error, required, className, children }): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={htmlFor} className='text-sm font-medium text-foreground'>
                {label}

                {required && (
                    <span className='ml-0.5 text-primary' aria-hidden='true'>
                        *
                    </span>
                )}
            </label>

            {children}

            {hint && !error && (
                <p className='text-xs text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p className='text-xs font-medium text-destructive' role='alert'>
                    {error}
                </p>
            )}
        </div>
    )
}
